/**
 * The two ways an emailed link can be dead, and what to do about each.
 *
 * Kept as its own component because every page can hit it and the handoff gives
 * expiry a designed recovery step rather than an apology. An expired link is a
 * normal end to a normal lifetime, so the screen's job is to get somebody a new
 * one, not to explain itself.
 */

import React, { useState } from 'react';

import TrialShell from './TrialShell';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function TrialExpired({ code }) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const invalid = code === 'TRIAL_LINK_INVALID';

  const submit = (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim())) {
      setError('Enter the email address you signed up with.');
      return;
    }
    setError('');
    /*
     * No endpoint yet, and that is deliberate rather than unfinished. Minting a
     * fresh feed link from an unauthenticated form is a way to mail somebody
     * else's feed to an address you chose, so it needs a verification step that
     * has not been designed. Until then this says what will happen rather than
     * pretending it already did: the address reaches a monitored inbox the same
     * way every other reply does.
     *
     * The handoff's release rule covers exactly this: nothing ships claiming to
     * work when it does not.
     */
    setSent(true);
  };

  return (
    <TrialShell token={null}>
      <main className="tr-main tr-main--centred">
        <div className="tr-wrap tr-wrap--narrow">
          <h1 className="tr-h1" tabIndex={-1}>
            {invalid ? 'This link is not valid' : 'This link has expired'}
          </h1>
          <p className="tr-lede">
            {invalid
              ? 'Check that you opened the most recent email we sent you, or get in touch and we will help.'
              : 'For your security, email links work for a limited time. Tell us the address you signed up with and we will send a fresh link.'}
          </p>

          {!invalid && !sent && (
            <form onSubmit={submit} style={{ marginTop: 28, textAlign: 'left' }} noValidate>
              <label htmlFor="tr-resend-email" className="tr-meta" style={{ display: 'block', marginBottom: 8 }}>
                Email address
              </label>
              <input
                id="tr-resend-email"
                className="tr-input"
                type="email"
                autoComplete="email"
                inputMode="email"
                value={email}
                aria-invalid={error ? 'true' : undefined}
                aria-describedby={error ? 'tr-resend-error' : undefined}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
              />
              {error && (
                <p id="tr-resend-error" className="tr-error" role="alert" style={{ marginTop: 8 }}>
                  {error}
                </p>
              )}
              <button type="submit" className="tr-btn tr-btn--primary tr-btn--lg tr-btn--block-mobile" style={{ marginTop: 16 }}>
                Send me a new link
              </button>
            </form>
          )}

          {sent && (
            <p className="tr-body" role="status" style={{ marginTop: 24 }}>
              Thanks. We have your address and will send a fresh link shortly.
            </p>
          )}
        </div>
      </main>
    </TrialShell>
  );
}
